import { useCallback, useMemo, useRef } from 'react';
import { BarWithErrorBarsController } from 'chartjs-chart-error-bars';
import { chartColors, chartColorsDarker, colors, ColorVariant } from '../../constants/colors';
import { secondsToMinutes } from 'date-fns';
import { Set, DataSet } from './types';

import { getEntityName, transposeArray } from '../../utilities/helpers';
import { makeBarOptions } from './barOptions';
import { CompareValues } from '../MultiFilter/MultiFilters.types';
import { DisplayOptionValue } from '../../page/genesis-analytics/useControls';
import { sortItems } from '../../utilities/helpers';
import { useCreateChartTooltip } from '../ChartTooltip/ChartTooltip';
import { ChartOptions } from 'chart.js';
import { capitalize } from 'lodash';
import pattern from 'patternomaly';

const barOptions = {
  fill: true,
  borderColor: 'white',
  borderRadius: 6,
  borderWidth: 1,
  tension: 0.3,
  groupPadding: 2,
  pointRadius: 0,
  errorBarWhiskerRatio: 0.1,
};

export type TransformKeys<K extends string> = (key: K, significant: boolean | null) => string;

export const fillDataSet =
  <K extends string>(
    set: Set[string],
    transformKeys: TransformKeys<K>,
    shouldSkipEmptyValues = true,
    displayOptionList: DisplayOptionValue[],
  ) =>
  (gen: Partial<AnalyticsTransformResult>) => {
    const duration = Number(gen?.averageDuration);

    if (!duration && shouldSkipEmptyValues) return;
    const amount = Number(gen.activitiesAmount);
    const minutes = secondsToMinutes(gen?.averageDuration || 0);
    set.significant = gen?.significant !== undefined ? gen?.significant : null;

    set.standardDeviation.push(gen?.standardDeviation || 0);

    set.dataPoints.push(gen?.dataPoints || []);

    if (gen?.min !== undefined) {
      set.mins?.push(gen?.min);
    }
    if (gen?.max !== undefined) {
      set.maxs?.push(gen?.max);
    }

    if (gen?.name) {
      const label = transformKeys(gen.name as K, set.significant) ?? 'unknown type';

      // set.phaseNames.push(gen.name);
      set.phaseNames.push(
        gen?.label?.split(' ')[2] +
          (set.significant !== null && displayOptionList.includes('statisticalSignificance')
            ? ` (${set.significant ? 'sig' : 'insig'})`
            : '') || gen.name,
      );

      set.labels.push(
        label, //+ (set.significant !== null && showSignificant ? ` (${set.significant ? 'sig' : 'insig'})` : ''),
      );
    }

    set.data.push(duration);

    set.datalabels.push(amount);
  };

export function buildScatters(input: number[][][], set: Set) {
  const setsNames = Object.keys(set);

  const scatters = transposeArray(input);
  const scatterChart: any[] = [];

  scatters.forEach((point, i) => {
    point.forEach((p, j) => {
      const k = setsNames[j];
      scatterChart.push({
        id: i + j,
        label: set[k].name + j + i,
        data: p.map((d, o) => ({
          x: o,
          y: d,
        })) as any,
        dataPoints: set[k].dataPoints,
        standardDeviation: set[k].standardDeviation,
        general: set,
        significant: set[k].significant,
        color: set[k].color,
        textColor: set[k].textColor,
      });
    });
  });

  return scatterChart;
}

type BarChartEngineProps<K extends string> = {
  sourceData: AnalyticsGenericDataFormat<K>[];
  compareBy: CompareValues;
  shouldSkipEmptyValues?: boolean;
  // maintainAspectRatio?: boolean;
  // responsive?: boolean;
  transformKeys: TransformKeys<K>;
  displayOptionList: DisplayOptionValue[];
  allFilters: AllFilters;
  unit?: string;
  extendOptions?: ChartOptions<'bar'>;
  isUtilizationChart?: boolean;
};

export function useBarChartEngine<K extends string>({
  sourceData,
  compareBy,
  transformKeys,
  shouldSkipEmptyValues = true,
  displayOptionList,
  allFilters,
  extendOptions,
  unit = 'minutes',
  isUtilizationChart = false,
}: BarChartEngineProps<K>) {
  const tooltipRef = useRef(document.createElement('div'));

  useCreateChartTooltip(tooltipRef);

  const availablePatterns: Array<
    | 'line-vertical'
    | 'zigzag-vertical'
    | 'diagonal-right-left'
    | 'diagonal'
    | 'line'
    | 'cross'
    | 'dot'
    | 'dash'
    | 'zigzag'
    | 'plus'
    | 'weave'
    | 'triangle'
    | 'cross-dash'
    | 'dot-dash'
    | 'disc'
    | 'ring'
    | 'diamond'
    | 'diamond-box'
  > = [
    'weave',
    'diagonal-right-left',
    'dot',
    'diagonal',
    'line-vertical',
    'dash',
    'zigzag',
    'plus',
  ];

  const getModalityIndex = (name: string) => {
    const modality = name.split('-')?.[0];
    switch (modality) {
      case 'robotic':
        return 0;
      case 'lap':
        return 1;
      case 'open':
        return 2;
      case 'other':
        return 3;
      case 'after_hours':
        return 4;
    }
  };

  const getColor = (name: string, i: number, isUtilizationChart = false, groupCount = 1000) => {
    if (isUtilizationChart) {
      const patternSize = 12;

      const modalityIdx = getModalityIndex(name);
      return modalityIdx == 0
        ? [...chartColors][i] // Solid Color for Robotic Modality
        : pattern.draw(
            availablePatterns[modalityIdx ?? 0],
            [...chartColors][i % groupCount],
            '#ffffff',
            patternSize,
          ); // Pattern for other modalities
    }
    return /rest/i.test(name) ? colors.neutralGray : [...chartColors][i];
  };

  // Ramtin: Old version before patterns (Keeping it for now in case we want to revert back)
  // const getColor = (name: string, i: number, isUtilizationChart = false, groupCount = 1000) => {
  //   if (isUtilizationChart) {
  //     return /rest/i.test(name)
  //       ? colors.neutralGray
  //       : ColorVariant(
  //           [...chartColors][i % groupCount],
  //           1 - Math.floor(i / groupCount) / (groupCount + 1),
  //         );
  //   }
  //   return /rest/i.test(name) ? colors.neutralGray : [...chartColors][i];
  // };

  const getDarkColor = (name: string, i: number, isUtilizationChart = false, groupCount = 1000) => {
    if (isUtilizationChart) {
      return /rest/i.test(name)
        ? colors.neutralGrayDark
        : ColorVariant(
            [...chartColorsDarker][i % groupCount],
            1 - Math.floor(i / groupCount) / (groupCount + 1),
          );
    }
    return /rest/i.test(name) ? colors.neutralGrayDark : [...chartColorsDarker][i];
  };

  const chart = useMemo(() => {
    const set: Set = {};
    // Get the count of unique groups based on the name split by '-'
    const groupCount = new window.Set(sourceData.map((t) => t.name.split('-')?.[1])).size;

    sourceData.forEach(({ name, context }, i) => {
      set[name] = {
        name,
        phaseNames: [],
        mins: [],
        maxs: [],
        color: getColor(name, i, isUtilizationChart, groupCount), // /rest/i.test(name) ? colors.neutralGray : [...chartColors][i],
        darkColor: getDarkColor(name, i, isUtilizationChart, groupCount),
        textColor: [...chartColors][i],
        dataPoints: [],
        standardDeviation: [],
        significant: null,
        data: [],
        labels: [],
        datalabels: [],
      };
      context.forEach(
        fillDataSet(set[name], transformKeys, shouldSkipEmptyValues, displayOptionList),
      );
    });

    const setsNames = Object.keys(set);

    const chartData: DataSet[] = setsNames.map((k, i) => ({
      id: i,
      label: set[k].name.split(' ').map(capitalize).join(' '),
      data: set[k].data,
      phaseNames: set[k].phaseNames,
      dataPoints: set[k].dataPoints,
      datalabels: set[k].datalabels,
      labels: set[k].labels,
      darkColor: set[k].darkColor,
      mins: set[k].mins,
      maxs: set[k].maxs,
      tooltipLabel: getEntityName({
        value: set[k].name,
        allFilters,
        compareBy,
      }),

      standardDeviation: set[k].standardDeviation,
      general: set,
      significant: set[k].significant,
      color: set[k].color,
      textColor: set[k].textColor,
    }));

    const dataItems = sortItems(chartData, compareBy);

    return { data: dataItems };
  }, [sourceData, compareBy, displayOptionList]);

  const getData = useCallback(() => {
    return {
      datasets: chart.data.map((item) => ({
        ...item,
        dataPoints: [],
        backgroundColor: sourceData.length > 1 ? item.color : chartColors[0],
        data: item.data.map((d, j) => ({
          y: d,
          x: isUtilizationChart ? item.phaseNames[j] : item.labels[j],
        })),
        ...barOptions,
      })),
    };
  }, [chart]);

  const getDataPointsData = useCallback(() => {
    const res = {
      datasets: chart.data.map((item, l) => ({
        ...item,
        backgroundColor: sourceData.length > 1 ? item.color : chartColors[0],
        data: item.data.map((d, j) => ({
          y: d,
          x: isUtilizationChart ? item.phaseNames[j] : item.labels[j],
        })),
        ...barOptions,
      })),
    };
    return res;
  }, [chart]);

  const getSDSData = useCallback(() => {
    return {
      datasets: chart.data.map((item, i) => ({
        ...item,
        dataPoints: [],
        data: item.data.map((d, j) => ({
          y: d,
          yMin: d - item?.standardDeviation?.[j],
          yMax: d + item?.standardDeviation?.[j],
          sds: item?.standardDeviation?.[j],
          x: isUtilizationChart ? item.phaseNames[j] : item.labels[j],
        })),
        type: BarWithErrorBarsController.id,
        backgroundColor: sourceData.length > 1 ? item.color : chartColors[0],
        ...barOptions,
      })),
    };
  }, [chart]);

  const getBothData = useCallback(() => {
    return {
      datasets: [
        ...chart.data.map((item, i) => ({
          ...item,
          stack: item.label,
          id: item.label + ' mean',
          label: item.label + ' mean',
          data: item.data.map((d, j) => ({
            y: d,
            yMin: d - item?.standardDeviation?.[j],
            yMax: d + item?.standardDeviation?.[j],
            x: item.labels[j],
          })),
          type: BarWithErrorBarsController.id,
          backgroundColor: sourceData.length > 1 ? item.color : chartColors[0],
          ...barOptions,
        })),
        ...chart.data.map((item) => ({
          ...item,
          dataPoints: [],
          stack: item.label,
          id: item.label + ' max',
          label: item.label + ' max',
          data: item.maxs,
          backgroundColor: sourceData.length > 1 ? item.color + '90' : chartColors[0],
          ...barOptions,
        })),
      ],
    };
  }, [chart]);

  const addValues = (min: boolean, max: boolean, datasets: Record<string, any>[]) => {
    const res = [
      ...datasets.map((item) => ({
        ...item,
        stack: isUtilizationChart ? item.label.split('-')?.[1] : item.label,
        // grouped: false,
        label: item.label + ' mean',
        order: 2,
      })),
      ...(!min
        ? []
        : datasets.map((item) => ({
            ...item,
            stack: item.label,
            datalabels: [],
            label: item.label + ' min',
            // grouped: false,
            backgroundColor: `${item.darkColor}`,
            data: item.mins,
            order: 1,
          }))),
      ...(!max
        ? []
        : datasets.map((item) => ({
            ...item,
            stack: item.label,
            datalabels: [],
            label: item.label + ' max',
            // grouped: false,
            backgroundColor: item.backgroundColor + 'AA',
            data: item.maxs,
            order: 1,
          }))),
    ];
    return res;
  };

  const _getBarChartData = () => {
    if (displayOptionList.includes('deviation') && displayOptionList.includes('dataPoints')) {
      return getBothData();
    }
    if (displayOptionList.includes('deviation')) {
      return getSDSData();
    }
    if (displayOptionList.includes('dataPoints')) {
      return getDataPointsData();
    }

    return getData();
  };
  const getBarChartData = () => {
    const data = _getBarChartData();
    return {
      ...data,
      datasets: addValues(
        displayOptionList.includes('min'),
        displayOptionList.includes('max'),
        data.datasets,
      ),
    };
  };

  const barChartOptions = makeBarOptions({
    chart,
    compareBy,
    unit,
    tooltipRef,
    allFilters,
    showDataLabels: displayOptionList.includes('sampleSize'),
    extendOptions,
    isUtilizationChart,
    displayOptionList,
  });

  return { getBarChartData, barChartOptions };
}
